import { MutableRefObject, useEffect, useRef } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

declare global {
    interface Window {
        turnstile?: {
            render: (el: HTMLElement, options: Record<string, unknown>) => string;
            reset: (id?: string) => void;
            remove: (id?: string) => void;
        };
    }
}

let loading: Promise<void> | null = null;

/** Cloudflare's script, loaded once for the page and only when asked for. */
function loadTurnstile(): Promise<void> {
    if (window.turnstile) return Promise.resolve();
    if (loading) return loading;
    loading = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => {
            loading = null;
            reject(new Error('turnstile failed to load'));
        };
        document.head.appendChild(script);
    });
    return loading;
}

/**
 * Cloudflare Turnstile, rendered explicitly. Most people never see a puzzle:
 * the widget decides in the background and hands back a token.
 */
export const Turnstile = ({
    siteKey,
    language,
    onToken,
    onError,
    resetRef,
    className,
}: {
    siteKey: string;
    language?: string;
    onToken: (token: string | null) => void;
    onError: () => void;
    resetRef?: MutableRefObject<(() => void) | null>;
    className?: string;
}) => {
    const el = useRef<HTMLDivElement>(null);
    // Latest callbacks without re-rendering the widget.
    const handlers = useRef({ onToken, onError });
    handlers.current = { onToken, onError };

    useEffect(() => {
        let id: string | undefined;
        let cancelled = false;
        loadTurnstile()
            .then(() => {
                if (cancelled || !el.current || !window.turnstile) return;
                id = window.turnstile.render(el.current, {
                    sitekey: siteKey,
                    theme: 'auto',
                    language: language || 'auto',
                    callback: (token: string) => handlers.current.onToken(token),
                    'expired-callback': () => handlers.current.onToken(null),
                    'error-callback': () => handlers.current.onError(),
                });
                if (resetRef) resetRef.current = () => window.turnstile?.reset(id);
            })
            .catch(() => handlers.current.onError());
        return () => {
            cancelled = true;
            if (id) window.turnstile?.remove(id);
            if (resetRef) resetRef.current = null;
        };
    }, [siteKey, language, resetRef]);

    return <div ref={el} className={className} data-testid="turnstile" />;
};
